import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Row, Col, Card, Input, Select, Spin, Empty, Tag, Space,
  Typography, Button, Drawer, Descriptions, Image, Statistic,
} from 'antd';
import {
  HomeOutlined, SearchOutlined, ReloadOutlined,
  EnvironmentOutlined, ExpandOutlined, FacebookOutlined,
} from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;

const API = '/api/v1/jj';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}` });

// Normalise a post into a flat display object
function normalise(p) {
  const parsed = p.parsed || {};
  const loc = parsed.location || {};
  const price = parsed.price || {};
  const floor = parsed.floorArea || {};
  const imgs = (p.fbImages || []).map(i => i.url).filter(Boolean);
  return {
    id: p._id,
    title: parsed.title || p.fbAuthorName || '—',
    price: price.amount,
    currency: price.currency || 'THB',
    period: price.period,
    area: loc.area,
    district: loc.district,
    nearBTS: loc.nearBTS,
    nearMRT: loc.nearMRT,
    propertyType: parsed.propertyType,
    bedrooms: parsed.bedrooms,
    bathrooms: parsed.bathrooms,
    floorArea: floor.value,
    furnishing: parsed.furnishing,
    petFriendly: parsed.petFriendly,
    contact: parsed.contact || {},
    amenities: parsed.amenities || [],
    images: imgs,
    fbPostUrl: p.fbPostUrl,
    fbAuthorName: p.fbAuthorName,
    rawText: p.fbRawText,
    crawledAt: p.crawledAt,
    isFavorite: p.isFavorite,
    tags: p.tags || [],
  };
}

function PropertyCard({ p, onClick }) {
  const { t } = useTranslation();
  const priceStr = p.price ? `฿${Number(p.price).toLocaleString()}${p.period ? `/${p.period}` : ''}` : t('houseRental.priceNeg', 'Negotiable');

  return (
    <Card
      hoverable onClick={() => onClick(p)}
      cover={
        p.images.length > 0 ? (
          <div style={{ height: 155, overflow: 'hidden' }}>
            <img src={p.images[0]} alt={p.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => { e.target.parentNode.style.display = 'none'; }} />
          </div>
        ) : (
          <div style={{ height: 155, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HomeOutlined style={{ fontSize: 36, color: '#ccc' }} />
          </div>
        )
      }
      size="small" bodyStyle={{ padding: '10px 12px' }}
    >
      <div style={{ display: 'flex', gap: 4, marginBottom: 5, flexWrap: 'wrap' }}>
        {p.propertyType && <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>{p.propertyType}</Tag>}
        {p.bedrooms != null && <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>{p.bedrooms} {t('houseRental.bed','bed')}</Tag>}
        {p.furnishing && <Tag style={{ fontSize: 11, margin: 0 }}>{p.furnishing}</Tag>}
      </div>
      <Text strong style={{ fontSize: 13, display: 'block' }} ellipsis>{p.title}</Text>
      {(p.area || p.nearBTS) && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          <EnvironmentOutlined /> {[p.area, p.nearBTS ? `BTS ${p.nearBTS}` : null].filter(Boolean).join(' · ')}
        </Text>
      )}
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong style={{ fontSize: 15, color: '#e8363a' }}>{priceStr}</Text>
        {p.floorArea && <Text type="secondary" style={{ fontSize: 11 }}>{p.floorArea} ㎡</Text>}
      </div>
    </Card>
  );
}

function PropertyDrawer({ post, onClose }) {
  const { t } = useTranslation();
  if (!post) return null;
  return (
    <Drawer
      title={<span><FacebookOutlined style={{ color: '#1877f2', marginRight: 6 }} />{post.title}</span>}
      open={!!post} onClose={onClose} width={540}
      extra={
        post.fbPostUrl && (
          <Button size="small" icon={<ExpandOutlined />} href={post.fbPostUrl} target="_blank">
            Facebook
          </Button>
        )
      }
    >
      {post.images.length > 0 && (
        <Image.PreviewGroup>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {post.images.slice(0, 8).map((url, i) => (
              <Image key={i} src={url} width={110} height={85} style={{ objectFit: 'cover', borderRadius: 4 }} />
            ))}
          </div>
        </Image.PreviewGroup>
      )}

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Statistic title={t('houseRental.price','Price')}
            value={post.price ? `฿${Number(post.price).toLocaleString()}` : '—'}
            valueStyle={{ fontSize: 15 }} />
        </Col>
        {post.bedrooms != null && <Col span={8}><Statistic title={t('houseRental.beds','Beds')} value={post.bedrooms} /></Col>}
        {post.floorArea && <Col span={8}><Statistic title={t('houseRental.area','Area')} value={`${post.floorArea} ㎡`} valueStyle={{ fontSize: 15 }} /></Col>}
      </Row>

      <Descriptions column={1} size="small" bordered style={{ marginBottom: 12 }}>
        {post.propertyType && <Descriptions.Item label={t('houseRental.type','Type')}>{post.propertyType}</Descriptions.Item>}
        {post.area && <Descriptions.Item label={t('houseRental.area','Area')}>{post.area}</Descriptions.Item>}
        {post.nearBTS && <Descriptions.Item label="BTS">{post.nearBTS}</Descriptions.Item>}
        {post.nearMRT && <Descriptions.Item label="MRT">{post.nearMRT}</Descriptions.Item>}
        {post.bathrooms != null && <Descriptions.Item label={t('houseRental.baths','Baths')}>{post.bathrooms}</Descriptions.Item>}
        {post.furnishing && <Descriptions.Item label={t('houseRental.furnishing','Furnishing')}>{post.furnishing}</Descriptions.Item>}
        {post.petFriendly != null && <Descriptions.Item label={t('houseRental.pets','Pets')}>{post.petFriendly ? '✅' : '❌'}</Descriptions.Item>}
        {post.contact?.phone && <Descriptions.Item label={t('houseRental.phone','Phone')}>{post.contact.phone}</Descriptions.Item>}
        {post.contact?.line && <Descriptions.Item label="LINE">{post.contact.line}</Descriptions.Item>}
        {post.contact?.whatsapp && <Descriptions.Item label="WhatsApp">{post.contact.whatsapp}</Descriptions.Item>}
        {post.fbAuthorName && <Descriptions.Item label={t('houseRental.postedBy','Posted by')}>{post.fbAuthorName}</Descriptions.Item>}
        {post.crawledAt && <Descriptions.Item label={t('houseRental.crawledAt','Crawled')}>{new Date(post.crawledAt).toLocaleDateString()}</Descriptions.Item>}
      </Descriptions>

      {post.amenities.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{t('houseRental.amenities','Amenities')}</Text>
          <div style={{ marginTop: 4 }}>
            {post.amenities.map((a, i) => <Tag key={i} style={{ marginBottom: 4 }}>{a}</Tag>)}
          </div>
        </div>
      )}

      {post.rawText && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>{t('houseRental.originalPost','Original FB Post')}</Text>
          <Paragraph style={{ fontSize: 12, marginTop: 4, background: '#fafafa', padding: 8, borderRadius: 4, maxHeight: 200, overflow: 'auto' }}>
            {post.rawText}
          </Paragraph>
        </div>
      )}
    </Drawer>
  );
}

export default function HouseAIRentalPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [bedsFilter, setBedsFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [total, setTotal] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/houseai-posts/stats`, { headers: h() });
      const d = await res.json();
      setStats(d);
    } catch { /* ignore */ }
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (search) params.set('search', search);
      if (typeFilter) params.set('propertyType', typeFilter);
      if (areaFilter) params.set('area', areaFilter);
      if (bedsFilter !== '') params.set('bedrooms', bedsFilter);

      const res = await fetch(`${API}/houseai-posts?${params}`, { headers: h() });
      const d = await res.json();
      const list = (d.posts || []).map(normalise);
      setPosts(list);
      setTotal(d.total || list.length);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, areaFilter, bedsFilter]);

  useEffect(() => { fetchStats(); fetchPosts(); }, [fetchStats, fetchPosts]);

  const areas = [...new Set(posts.map(p => p.area).filter(Boolean))].sort();

  const avgPrice = posts.filter(p => p.price > 0).length
    ? Math.round(posts.filter(p => p.price > 0).reduce((s, p) => s + p.price, 0) / posts.filter(p => p.price > 0).length)
    : 0;

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <FacebookOutlined style={{ color: '#1877f2', marginRight: 8 }} />
            {t('houseRental.pageTitle', 'Property Search — Bangkok')}
          </Title>
          <Text type="secondary">{t('houseRental.fbSource', 'Facebook Group · AI-parsed · HouseAI MongoDB')}</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => { fetchStats(); fetchPosts(); }} loading={loading} size="small">
          {t('dash.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Stats */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        {[
          { label: t('houseRental.statTotal','Total'), value: stats.total || total, color: '#1890ff' },
          { label: t('houseRental.statActive','Active'), value: stats.active || posts.length, color: '#52c41a' },
          { label: t('houseRental.statAvgPrice','Avg Price'), value: avgPrice ? `฿${avgPrice.toLocaleString()}` : '—', color: '#722ed1' },
          { label: t('houseRental.statAreas','Areas'), value: areas.length, color: '#fa8c16' },
        ].map((s, i) => (
          <Col span={6} key={i}>
            <Card size="small" style={{ borderTop: `3px solid ${s.color}` }}>
              <Statistic title={<Text style={{ fontSize: 12 }}>{s.label}</Text>} value={s.value} valueStyle={{ color: s.color, fontSize: 20 }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={8} align="middle">
          <Col span={8}>
            <Input prefix={<SearchOutlined />}
              placeholder={t('houseRental.searchPlaceholder','Search title, location, BTS...')}
              value={search} onChange={e => setSearch(e.target.value)} allowClear />
          </Col>
          <Col span={4}>
            <Select value={typeFilter} onChange={setTypeFilter} style={{ width: '100%' }}
              placeholder={t('houseRental.allTypes','All Types')} allowClear>
              {['condo','apartment','house','townhouse','villa','studio'].map(v => (
                <Option key={v} value={v}>{v}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select value={areaFilter} onChange={setAreaFilter} style={{ width: '100%' }}
              placeholder={t('houseRental.allAreas','All Areas')} allowClear showSearch>
              {areas.map(a => <Option key={a} value={a}>{a}</Option>)}
            </Select>
          </Col>
          <Col span={4}>
            <Select value={bedsFilter} onChange={setBedsFilter} style={{ width: '100%' }}
              placeholder={t('houseRental.anyBeds','Any Beds')} allowClear>
              <Option value="0">Studio</Option>
              <Option value="1">1 {t('houseRental.bed','bed')}</Option>
              <Option value="2">2 {t('houseRental.beds','beds')}</Option>
              <Option value="3">3 {t('houseRental.beds','beds')}</Option>
              <Option value="4">4+ {t('houseRental.beds','beds')}</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>{total} {t('houseRental.results','results')}</Text>
          </Col>
        </Row>
      </Card>

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : posts.length === 0 ? (
        <Empty description={t('houseRental.noProperties','No properties found')} style={{ marginTop: 60 }} />
      ) : (
        <Row gutter={[12, 12]}>
          {posts.map(p => (
            <Col key={p.id} xs={24} sm={12} md={8} lg={6}>
              <PropertyCard p={p} onClick={setSelected} />
            </Col>
          ))}
        </Row>
      )}

      <PropertyDrawer post={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
